import { useEffect, useRef, useState, type JSX } from 'react';

import { useMutation } from '@apollo/client/react';
import { Alert } from '@admin-platform/ui/components';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/Button/Button';

import CONFIRM_EMAIL_CHANGE from './mutations/confirmEmailChange.mutation.graphql';
import type {
  ConfirmEmailChangeMutation,
  ConfirmEmailChangeMutationVariables,
} from './mutations/confirmEmailChange.mutation.graphql';
import styles from './VerifyEmailChange.module.css';
import { AuthFooter } from '../../components/AuthCardChrome/AuthCardChrome';
import RevvlyMark from '../../components/Layout/revvly-mark.svg?react';

type Status = 'verifying' | 'success' | 'error';

export default function VerifyEmailChange(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string>(
    'This confirmation link is invalid or has expired.',
  );

  const [confirmEmailChange] = useMutation<
    ConfirmEmailChangeMutation,
    ConfirmEmailChangeMutationVariables
  >(CONFIRM_EMAIL_CHANGE);

  // Confirm once per distinct token. Keying the guard by the token (rather than a
  // one-shot boolean) means StrictMode's double-invoke is still deduped, but a new
  // token arriving while the page stays mounted re-runs the confirmation instead
  // of being skipped forever. The token is single-use, so we never re-confirm the
  // same one.
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    if (lastTokenRef.current === token) {
      return;
    }
    lastTokenRef.current = token;
    setStatus('verifying');

    confirmEmailChange({ variables: { token } })
      .then(({ data }) => {
        const result = data?.confirmEmailChange;
        if (result?.success) {
          setConfirmedEmail(result.email);
          setStatus('success');
        } else {
          setStatus('error');
        }
      })
      .catch((err: unknown) => {
        setErrorText(
          err instanceof Error && err.message
            ? err.message
            : 'This confirmation link is invalid or has expired.',
        );
        setStatus('error');
      });
  }, [token, confirmEmailChange]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <RevvlyMark className={styles.mark} aria-hidden="true" />
        <h1 className={styles.logo}>Revvly</h1>
        <p className={styles.subtitle}>Confirm your new email</p>

        {status === 'verifying' && <p className={styles.verifying}>Confirming your new email…</p>}

        {status === 'success' && (
          <>
            <Alert type="success" className={styles.alertBlock}>
              Your email address has been updated
              {confirmedEmail ? ` to ${confirmedEmail}` : ''}. Please sign in again with your new
              email.
            </Alert>
            <div className={styles.actions}>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Go to login
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert type="error" className={styles.alertBlock}>
              {errorText}
            </Alert>
            <div className={styles.actions}>
              <Button variant="primary" onClick={() => navigate('/account')}>
                Back to your account
              </Button>
            </div>
          </>
        )}
        <AuthFooter />
      </div>
    </div>
  );
}
